import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ImageUp, Loader2, ScanLine, X } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { nbaToday } from "@/lib/format";
import { PageTitle, Panel } from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ReviewPanel } from "@/components/upload/ReviewPanel";

export default function Upload() {
  const [day, setDay] = useState(nbaToday(-1));
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [drag, setDrag] = useState(false);
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const addFiles = (list) => {
    const ok = [...list].filter((f) => ["image/png", "image/jpeg"].includes(f.type));
    if (ok.length < list.length) toast.error("Only PNG or JPG images");
    setFiles((prev) => [...prev, ...ok].slice(0, 6));
  };

  const extract = async () => {
    setBusy(true); setResult(null);
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    form.append("game_date", day);
    try {
      const { data } = await api.post("/screenshots/extract", form, { timeout: 180000 });
      setResult(data);
      toast.success("Box score read — review before saving");
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };

  const reset = () => { setFiles([]); setResult(null); };

  return (
    <div className="space-y-6">
      <PageTitle overline="Vision AI import" title="Box-score screenshot">
        <label className="text-xs text-slate-400 uppercase tracking-widest">Game date</label>
        <Input data-testid="upload-date-input" type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-auto bg-slate-900/70" />
      </PageTitle>
      <Panel className="p-5 sm:p-6">
        <label data-testid="upload-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 cursor-pointer transition-colors ${drag ? "border-blue-500 bg-blue-950/30" : "border-slate-700 hover:border-slate-500"}`}>
          <ImageUp className="h-8 w-8 text-blue-400" />
          <span className="font-semibold">Drop PNG/JPG box-score images or click to choose</span>
          <span className="text-xs text-slate-500">Up to 6 images, 8 MB each — both teams can be in separate images</span>
          <input data-testid="upload-file-input" type="file" accept="image/png,image/jpeg" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </label>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-4" data-testid="upload-previews">
            {files.map((f, i) => (
              <div key={i} className="relative h-24 w-36 rounded-md overflow-hidden border border-slate-700">
                <img src={previews[i]} alt={f.name} className="h-full w-full object-cover" />
                <button data-testid={`remove-file-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-slate-950/80 rounded p-0.5"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <Button data-testid="extract-btn" onClick={extract} disabled={!files.length || busy} className="bg-blue-600 hover:bg-blue-500">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Reading box score…</> : <><ScanLine className="h-4 w-4 mr-2" />Read with AI</>}
          </Button>
          {(files.length > 0 || result) && <Button data-testid="upload-reset-btn" variant="ghost" onClick={reset}>Clear</Button>}
        </div>
      </Panel>
      {result && <ReviewPanel key={JSON.stringify(result).length} result={result} onSaved={reset} />}
    </div>
  );
}
